// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './index.module.scss';

import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CheckboxField from 'components/atoms/CheckboxField';
import { CheckBox } from 'components/atoms/CheckBox';
import { IconChevronDown } from 'components/atoms/icons/IconChevronDown';
import { IconChevronUp } from 'components/atoms/icons/IconChevronUp';
import {
  getApiPath,
  mergeClasses,
  normalizeNumber,
  objectFetch,
} from 'util/commons';
import IconShipping from 'components/atoms/icons/IconShipping';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import IconCalendarTablet from 'components/atoms/icons/IconCalendarTablet';
import { ConfirmSelectEmissionItemsModal } from '../ConfirmSelectEmissionItemsModal';
import { ReportAbnormalityModal } from '../ReportAbnormalityModal';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  collectEmissionItems,
  getEmissionsBySiteAndRoute,
  getRoutesCollectedTotalWeight,
} from 'apis/collect_route';
import { getFormatDateTime } from 'ts/formatDate';
import { useSelector } from 'react-redux';
import { selectCollectingRouteSites } from 'redux/slices/collectingRoute';
import { Path } from 'routes';
import { Emission, EmissionItem } from 'types/routeManagementTypes';
import Portal from 'components/atoms/Portal';
import { toast } from 'react-toastify';
import { useCheckRouteCollectProcess } from 'util/hooks/useCheckRouteCollectProcess';
import CollectEmissionsSelectDriverModal from '../CollectEmissionsSelectDriverModal';
import { selectUser } from 'redux/slices/userSlice';
import { RoleType } from 'util/Enums';
import { CollectingRouteSession, isRouteIdExist } from 'util/collectingRoute';

type ConfirmSelectEmissionItemsProps = {
  setHasCollectedSuccess: (value: boolean) => void;
};

const ConfirmSelectEmissionItems: React.FC<ConfirmSelectEmissionItemsProps> = ({
  setHasCollectedSuccess,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const collectingRouteSites = useSelector(selectCollectingRouteSites);

  const { routeId, siteId } = useParams() as {
    routeId: string;
    siteId: string;
  };
  const { state } = useLocation();

  useCheckRouteCollectProcess();

  const [emissionItems, setEmissionItems] = useState<EmissionItem[]>([]);

  const allEmissionIdsByItemId = useMemo(
    () =>
      Object.fromEntries(
        emissionItems.map((item) => [
          item.wasteUnitId,
          item.emissions.map((e) => e.emissionId),
        ])
      ),
    [emissionItems]
  );

  const { areaId, areaName } = useMemo(() => {
    const currentSite = collectingRouteSites?.sites.find(
      (site) => site.id === siteId
    );

    return {
      areaId: currentSite?.area.id || '',
      areaName: currentSite?.area.name || '',
    };
  }, [collectingRouteSites, siteId]);

  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<
    Record<
      string,
      {
        name: string;
        emissions: Emission[];
      }
    >
  >({});

  const user = useSelector(selectUser);
  const [isShowConfirmModal, setIsShowConfirmModal] = useState<boolean>(false);
  const [isShowDriverModal, setIsShowDriverModal] = useState<boolean>(false);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [reportAbnormalityModal, setReportAbnormalityModal] = useState<
    | {
        name: string;
        emissionsIds: string[];
      }
    | undefined
  >();

  const [filterValue, setFilterValue] = useState<{
    dateFrom: string;
    dateTo: string;
  }>({
    dateFrom: '',
    dateTo: '',
  });

  const handleFetchData = async () => {
    if (routeId && siteId) {
      getEmissionsBySiteAndRoute({
        query: {
          routeId,
          siteId,
          dateFrom: filterValue.dateFrom,
          dateTo: filterValue.dateTo,
        },
        successCallback: (data) => {
          setEmissionItems(data);
        },
        failedCallback: () => {
          history.push(
            `${getApiPath(Path.routeSiteSelection, { routeId })}${
              window.location.search
            }`
          );
        },
      });
    }
  };

  useEffect(() => {
    handleFetchData();
  }, [routeId, siteId, filterValue.dateFrom, filterValue.dateTo]);

  const emissionsMap = useMemo(() => {
    const map = new Map<string, Emission>();
    emissionItems.forEach((item) => {
      item.emissions.forEach((emission) => {
        map.set(emission.emissionId, emission);
      });
    });
    return map;
  }, [emissionItems]);

  const selectedEmissionIdsMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    Object.entries(selectedItems).forEach(([itemId, item]) => {
      map.set(itemId, new Set(item.emissions.map((e) => e.emissionId)));
    });
    return map;
  }, [selectedItems]);

  const isAllItemsSelected = useMemo(() => {
    if (
      Object.keys(selectedItems).length !== emissionItems.length ||
      emissionItems.length === 0
    ) {
      return false;
    }

    return Object.entries(selectedItems).every(([itemId, item]) => {
      const totalEmissionsForItem = allEmissionIdsByItemId[itemId]?.length || 0;
      return item.emissions.length === totalEmissionsForItem;
    });
  }, [selectedItems, emissionItems, allEmissionIdsByItemId]);

  const getSelectAllCheckedStyle = useMemo(() => {
    const hasSelectedItems = Object.keys(selectedItems).length > 0;

    if (!hasSelectedItems) {
      return 'checked';
    }

    if (isAllItemsSelected) {
      return 'checked';
    }

    return 'partial';
  }, [selectedItems, isAllItemsSelected]);

  const quantity = useMemo(() => {
    return Object.values(selectedItems)
      .map((e) => e.emissions)
      .flat().length;
  }, [selectedItems]);

  const totalWeight = useMemo(() => {
    return Object.values(selectedItems)
      .flatMap((item) => item.emissions)
      .reduce((total, emission) => {
        return total + Number(emission.weight);
      }, 0);
  }, [selectedItems, emissionsMap]);

  const isExpandedItem = (id: string) => {
    return expandedIds.includes(id);
  };

  const handleSelectEmissionId = (itemId, emission) => {
    setSelectedItems((prev) => {
      if (itemId in prev) {
        if (
          !prev[itemId].emissions
            .map((e) => e.emissionId)
            .includes(emission.emissionId)
        ) {
          return {
            ...prev,
            [itemId]: {
              ...prev[itemId],
              emissions: [...prev[itemId].emissions, emission],
            },
          };
        }
        const updatedEmissions = prev[itemId].emissions.filter(
          (e) => e.emissionId !== emission.emissionId
        );
        if (updatedEmissions.length === 0) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [itemId]: _, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [itemId]: {
            ...prev[itemId],
            emissions: updatedEmissions,
          },
        };
      }

      return {
        ...prev,
        [itemId]: {
          name:
            emissionItems.find((item) => item.wasteUnitId === itemId)
              ?.wasteUnitName || '',
          emissions: [emission],
        },
      };
    });
  };

  const handleSelectItem = (item: EmissionItem) => {
    setSelectedItems((prev) => {
      if (item.wasteUnitId in prev) {
        const isFullySelected =
          prev[item.wasteUnitId].emissions.length ===
          allEmissionIdsByItemId[item.wasteUnitId].length;

        if (isFullySelected) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [item.wasteUnitId]: _, ...rest } = prev;
          return rest;
        }

        return {
          ...prev,
          [item.wasteUnitId]: {
            name: item.wasteUnitName,
            emissions: item.emissions,
          },
        };
      }

      return {
        ...prev,
        [item.wasteUnitId]: {
          name: item.wasteUnitName,
          emissions: item.emissions,
        },
      };
    });
  };

  const handleSelectAllItems = () => {
    if (isAllItemsSelected) {
      setSelectedItems({});
    } else {
      const allItems = emissionItems.reduce((acc, item) => {
        acc[item.wasteUnitId] = {
          name: item.wasteUnitName,
          emissions: item.emissions,
        };
        return acc;
      }, {} as Record<string, { name: string; emissions: Emission[] }>);
      setSelectedItems(allItems);
    }
  };

  const selectEmissionIds = useMemo(() => {
    return Object.values(selectedItems)
      .flatMap((item) => item.emissions)
      .map((emission) => emission.emissionId);
  }, [selectedItems]);

  const handleCollectEmissionItems = (isCompleted: boolean) => {
    collectEmissionItems({
      formData: {
        routeId: isRouteIdExist(routeId) ? Number(routeId) : undefined,
        areaId: areaId,
        emissionIds: selectEmissionIds,
        ...(!!selectedDriverId && { driverId: selectedDriverId }),
      },
      successCallback: () => {
        toast.success(t('messages.M_087'));
        setIsShowConfirmModal(false);
        handleCloseDriverModal();
        setSelectedItems({});
        CollectingRouteSession.addProcessedSite({
          routeId,
          siteId,
        });
        getRoutesCollectedTotalWeight();

        if (isCompleted) {
          history.push(
            `${getApiPath(Path.routeSiteSelection, {
              routeId: objectFetch(state, 'routeId') || routeId,
            })}${window.location.search}`,
            {
              refreshSites: true,
            }
          );
        } else {
          handleFetchData();
          setHasCollectedSuccess(true);
        }
      },
    });
  };

  const handleDriverSubmit = (driverId: string) => {
    setSelectedDriverId(driverId);
    setIsShowConfirmModal(true);
  };

  const handleCloseDriverModal = () => {
    setSelectedDriverId('');
    setIsShowDriverModal(false);
  };

  const getCheckedStyle = (itemId) => {
    if (itemId in selectedItems) {
      return selectedItems[itemId].emissions.length <
        allEmissionIdsByItemId[itemId].length
        ? 'partial'
        : 'checked';
    }
    return 'checked';
  };

  return (
    <div className={styles.selectEmissionItems}>
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.actionBar}>
            <TableFilterCalendar
              wrapperClassName={styles.DateRangePickerWrapper}
              containerClassName={styles.DateRangePicker}
              expanded={{
                placeholder: t('common.placeholder.select_date_range'),
                icon: <IconCalendarTablet />,
              }}
              fullWidth
              value={[filterValue.dateFrom, filterValue.dateTo]}
              onChange={(value) => {
                if (value && value[0] && value[1]) {
                  setFilterValue((prev) => ({
                    ...prev,
                    dateFrom: value[0] || '',
                    dateTo: value[1] || '',
                  }));
                } else if (!value) {
                  setFilterValue({
                    dateFrom: '',
                    dateTo: '',
                  });
                }
              }}
              chosenDatesLimit={{
                limit: 60,
                required: true,
              }}
              fontSizeType="large"
              portalPositionPoint={(ref) => ref}
            />
            <div className={styles.selectAll}>
              <CheckboxField
                title={t('confirm_select_route_item.select_all')}
                input={{
                  checked: Object.keys(selectedItems).length > 0,
                  onChange: () => handleSelectAllItems(),
                }}
                checkedStyle={getSelectAllCheckedStyle}
              />
            </div>
          </div>
        </div>

        <div className={styles.body}>
          <div className={styles.items}>
            {emissionItems.length ? (
              emissionItems.map((item) => (
                <div key={item.wasteUnitId} className={styles.itemCard}>
                  <div className={styles.itemInfo}>
                    <div className={styles.selectItem}>
                      <CheckboxField
                        input={{
                          id: String(item.wasteUnitId),
                          checked: Object.keys(selectedItems).includes(
                            String(item.wasteUnitId)
                          ),
                          onChange: () => handleSelectItem(item),
                        }}
                        size="large"
                        checkedStyle={getCheckedStyle(item.wasteUnitId)}
                      />
                      <div className={styles.itemHeader}>
                        <div className={styles.itemName}>
                          {item.wasteUnitName}
                        </div>
                        <button
                          className={styles.reportAbnormalButton}
                          onClick={() => {
                            setReportAbnormalityModal({
                              name: item.wasteUnitName,
                              emissionsIds: item.emissions.map(
                                (e) => e.emissionId
                              ),
                            });
                          }}
                        >
                          {t('confirm_select_route_item.mark_abnormal')}
                        </button>
                      </div>
                    </div>

                    <div className={styles.cardRight}>
                      <div className={styles.overall}>
                        <div className={styles.quantity}>
                          <div className={styles.quantityTitle}>
                            {t('confirm_select_route_item.overall.quantity')}
                          </div>
                          <div className={styles.quantityContent}>
                            <div className={styles.quantityNumber}>
                              {normalizeNumber({
                                value: item.emissionsCount,
                              })}
                            </div>
                            <div className={styles.unit}>
                              {t(
                                'confirm_select_route_item.overall.quantity_unit'
                              )}
                            </div>
                          </div>
                        </div>
                        <div className={styles.weight}>
                          <div className={styles.weightTitle}>
                            {t('confirm_select_route_item.overall.weight')}
                          </div>
                          <div className={styles.weightContent}>
                            <div className={styles.weightNumber}>
                              {normalizeNumber({
                                value: item.totalWeight,
                              })}
                            </div>
                            <div className={styles.unit}>kg</div>
                          </div>
                        </div>
                      </div>

                      <div
                        className={styles.expand}
                        onClick={() => {
                          setExpandedIds((prev) =>
                            prev.includes(item.wasteUnitId)
                              ? prev.filter((x) => x !== item.wasteUnitId)
                              : [...prev, item.wasteUnitId]
                          );
                        }}
                      >
                        {isExpandedItem(item.wasteUnitId) ? (
                          <IconChevronUp size={28} strokeColor="#9f9f9f" />
                        ) : (
                          <IconChevronDown size={28} strokeColor="#9f9f9f" />
                        )}
                      </div>
                    </div>
                  </div>

                  {isExpandedItem(item.wasteUnitId) && (
                    <div className={styles.expandList}>
                      {item.emissions
                        .filter((emission) => Number(emission.weight) !== 0)
                        .map((emission) => {
                          const selectedIds = selectedEmissionIdsMap.get(
                            String(item.wasteUnitId)
                          );
                          const isSelected =
                            selectedIds?.has(emission.emissionId) ?? false;

                          return (
                            <div
                              key={emission.emissionId}
                              className={mergeClasses(styles.expandItem, {
                                [styles.selected]: isSelected,
                              })}
                              onClick={() =>
                                handleSelectEmissionId(
                                  item.wasteUnitId,
                                  emission
                                )
                              }
                            >
                              <CheckBox
                                id={String(emission.emissionId)}
                                checked={isSelected}
                                onChange={(e) => {
                                  handleSelectEmissionId(
                                    item.wasteUnitId,
                                    emission
                                  );
                                  e.stopPropagation();
                                }}
                                checkedStyle="greenOutline"
                              />

                              <div className={styles.expandItemInfo}>
                                <div className={styles.weight}>
                                  <span className={styles.weightNumber}>
                                    {normalizeNumber({
                                      value: emission.weight,
                                    })}
                                  </span>
                                  <span className={styles.weightUnit}>kg</span>
                                </div>

                                <div className={styles.dateTime}>
                                  <span>
                                    {getFormatDateTime(emission.datetime)}{' '}
                                  </span>
                                  <span>登録</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className={styles.empty}>
                <IconShipping size={52} strokeColor="#4d4d4d" />
                <div className={styles.emptyText}>
                  {t('confirm_select_route_item.empty')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.bottom}>
        <div className={styles.bottomContent}>
          <div className={styles.overall}>
            <div className={styles.quantity}>
              <div className={styles.quantityTitle}>
                {t('confirm_select_route_item.overall.selected_count')}
              </div>
              <div className={styles.quantityContent}>
                <div className={styles.quantityNumber}>
                  {normalizeNumber({
                    value: quantity,
                  })}
                </div>
                <div className={styles.unit}>
                  {t('confirm_select_route_item.overall.selected_count_unit')}
                </div>
              </div>
            </div>
            <div className={styles.weight}>
              <div className={styles.weightTitle}>
                {t('confirm_select_route_item.overall.total_weight')}
              </div>
              <div className={styles.weightContent}>
                <div className={styles.weightNumber}>
                  {normalizeNumber({
                    value: totalWeight,
                  })}
                </div>
                <div className={styles.unit}>kg</div>
              </div>
            </div>
          </div>

          <button
            className={styles.submitButton}
            onClick={() => {
              [RoleType.AdminSite, RoleType.AdminOperator].includes(
                user.role as RoleType
              )
                ? setIsShowDriverModal(true)
                : setIsShowConfirmModal(true);
            }}
            disabled={Object.keys(selectedItems).length === 0}
          >
            {t('confirm_select_route_item.submit_button')}
          </button>
        </div>
      </div>

      {isShowDriverModal && (
        <CollectEmissionsSelectDriverModal
          closeModal={handleCloseDriverModal}
          onSubmit={handleDriverSubmit}
          routeId={routeId}
        />
      )}

      {isShowConfirmModal && (
        <ConfirmSelectEmissionItemsModal
          quantity={quantity}
          totalWeight={totalWeight}
          items={Object.fromEntries(
            Object.entries(selectedItems).map(([itemId, item]) => [
              itemId,
              {
                name: item.name,
                weight: item.emissions
                  .reduce((total, e) => total + Number(e.weight), 0)
                  .toString(),
              },
            ])
          )}
          onSelectItems={(isCompleted) => {
            handleCollectEmissionItems(isCompleted);
          }}
          onClose={() => setIsShowConfirmModal(false)}
        />
      )}

      {reportAbnormalityModal && (
        <Portal>
          <ReportAbnormalityModal
            area={{ name: areaName, id: areaId }}
            targetIds={reportAbnormalityModal.emissionsIds}
            onclose={() => setReportAbnormalityModal(undefined)}
          />
        </Portal>
      )}
    </div>
  );
};

export default ConfirmSelectEmissionItems;
